import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import BoxCard from './BoxCard';
import AddBoxDialog from './AddBoxDialog';
import { useBoxes } from '../hooks/useBoxes';
import { apiUtils } from '../utils/apiUtils';

export default function BoxesGrid({ onBoxClick }) {
  const { data: boxes, loading, error, retry } = useBoxes();
  const [addBoxDialogOpen, setAddBoxDialogOpen] = useState(false);

  const handleBoxAdded = (newBox) => {
    retry();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    const canRetry = apiUtils.isRetryableError(error);
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            canRetry ? (
              <Button
                color="inherit"
                size="small"
                onClick={retry}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            ) : null
          }
        >
          {apiUtils.formatErrorMessage(error)}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{
        mb: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Monitoring Boxes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {boxes?.length || 0} boxes total
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddBoxDialogOpen(true)}
          size="large"
          sx={{
            minWidth: 140,
            height: 48
          }}
        >
          Add Box
        </Button>
      </Box>

      {!boxes || boxes.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No boxes found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Get started by adding your first monitoring box
          </Typography>
          <Button
            variant="outlined"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setAddBoxDialogOpen(true)}
          >
            Add Your First Box
          </Button>
        </Box>
      ) : (
        <Grid
          container
          spacing={3}
          justifyContent="flex-start"
          alignItems="stretch"
          sx={{
            width: '100%',
            margin: 0
          }}
        >
          {boxes.map((box, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={box.box_id || index}
              sx={{
                display: 'flex',
                height: '86%'
              }}
            >
              <BoxCard
                box={box}
                onBoxClick={onBoxClick}
              />
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Box
              sx={{
                height: '86%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
              onClick={() => setAddBoxDialogOpen(true)}
            >
              <AddIcon
                sx={{
                  fontSize: 48,
                  color: 'text.secondary',
                  mb: 2
                }}
              />
              <Typography variant="body1" color="text.secondary" align="center">
                Add New Box
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      <Fab
        color="primary"
        aria-label="add box"
        onClick={() => setAddBoxDialogOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          display: { xs: 'flex', sm: 'none' }
        }}
      >
        <AddIcon />
      </Fab>

      <AddBoxDialog
        open={addBoxDialogOpen}
        onClose={() => setAddBoxDialogOpen(false)}
        onBoxAdded={handleBoxAdded}
      />
    </Box>
  );
}